import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'convex/react';
import pig1 from '../../../assets/graphics/pig1.gif';
import { api } from '../../../convex/_generated/api';
import { BaseContext } from '../../../shared/base-context';
import { Id } from '../../../convex/_generated/dataModel';
import { Button } from '../../../catalyst-components/button';
import { useStableQuery } from '../../../shared/hooks/useStableQuery';
import { parseMarkdown } from '../../../shared/helpers/markdown';
import { useInnerWeatherQuote } from '../../../shared/hooks/useInnerWeatherQuote';
import { AcornIcon } from '../../../shared/components/AcornIcon';
import { CheckinModal } from './CheckinModal';

export const CheckinBanner: React.FC = () => {
  const { t } = useTranslation();
  const { convexUserId } = useContext(BaseContext);

  const pendingCheckin = useStableQuery(
    api.user_checkins.getPendingCheckin,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  const userProgress = useStableQuery(
    api.user_progress.getByUserId,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  const quote = useInnerWeatherQuote(
    userProgress?.n_score ?? 50,
    userProgress?.wb_score ?? 50,
    userProgress?.se_score ?? 50
  );

  const acornConfig = useStableQuery(api.acorns.getConfig);
  const checkinReward = acornConfig?.checkin_reward ?? 3;

  const markCheckinShown = useMutation(api.user_checkins.markCheckinShown);
  const dismissCheckin = useMutation(api.user_checkins.dismissCheckin);

  const [showModal, setShowModal] = useState(false);

  // Mark as shown when component mounts with a pending checkin
  useEffect(() => {
    if (pendingCheckin?.checkin && convexUserId && !pendingCheckin.userCheckinId) {
      markCheckinShown({
        user_id: convexUserId as Id<"users">,
        checkin_id: pendingCheckin.checkin._id,
        sessions_at_checkin: pendingCheckin.completedSessionsCount,
      });
    }
  }, [pendingCheckin, convexUserId, markCheckinShown]);

  if (!pendingCheckin?.checkin) {
    return null;
  }

  const { checkin } = pendingCheckin;

  const handleStartCheckin = () => {
    setShowModal(true);
  };

  const handleDismiss = async () => {
    if (!convexUserId) return;

    await dismissCheckin({
      user_id: convexUserId as Id<"users">,
      checkin_id: checkin._id,
      sessions_at_checkin: pendingCheckin.completedSessionsCount,
    });
  };

  return (
    <>
      <div className="w-full border-2 border-primary/30 bg-gradient-to-b from-primary/5 to-accent/5 p-8 rounded-xl shadow-sm animate-fade-in">
        <div className="flex flex-col items-center text-center">
          {/* Pig Animation */}
          <div className="w-32 h-32 mb-6 animate-zoom-in">
            <img
              src={pig1}
              alt="Celebrating pig"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Celebration Header */}
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            {t('checkin.title')}
          </h2>

          {/* Greeting Message */}
          <div
            className="text-lg text-muted-foreground leading-relaxed max-w-lg mb-4 whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: parseMarkdown(checkin.greeting) }}
          />

          {/* Inner Weather Quote */}
          {quote && (
            <div className="text-sm italic text-muted-foreground/80 max-w-md mb-6">
              &ldquo;{quote.quote}&rdquo; &mdash; {quote.author}
            </div>
          )}

          {/* Acorn Reward Teaser */}
          {checkinReward > 0 && (
            <div className="flex items-center gap-2 text-sm text-primary font-medium mb-6">
              <AcornIcon size={18} className="text-primary" />
              <span>{t('checkin.rewardMessage', { checkinReward })}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              color="primary"
              onClick={handleStartCheckin}
              className="px-8 py-3 text-lg font-semibold rounded-full"
            >
              {t('checkin.primaryButton')}
            </Button>
            <Button
              color="secondary"
              onClick={handleDismiss}
              className="px-6 py-3 rounded-full"
            >
              {t('checkin.secondaryButton')}
            </Button>
          </div>
        </div>
      </div>

      <CheckinModal
        open={showModal}
        onClose={() => setShowModal(false)}
        checkin={checkin}
        sessionsAtCheckin={pendingCheckin.completedSessionsCount}
        checkinReward={checkinReward}
      />
    </>
  );
};
